import { useCallback } from 'react'
import { isCurrentRequestBot } from 'actions/SystemActions'
import config from 'constants/config'
import { Experiments } from 'constants/experiments'
import useExperiment from 'hooks/Experiments/useExperiment'
import { useAppSelector } from 'hooks/reduxHooks'
import { canUseGuestCheckout, type GuestCheckoutGuardInput, type GuestCheckoutRollout } from 'lib/checkout/guestCheckout'
import { isGhaVerifierUserAgent } from 'lib/metasearch/ghaLoadingDiagnostics'

type GuestCheckoutPurchase = Pick<GuestCheckoutGuardInput, 'isWalledGarden' | 'isMultiRoom' | 'itemTypes'>

interface GuestCheckoutEligibility {
  /** Whether this purchase satisfies the stable guest-checkout constraints. */
  isEligiblePurchase: (purchase: GuestCheckoutPurchase) => boolean
  /** Whether this exact purchase can proceed to checkout without signing in. */
  canProceedAsGuest: (purchase: GuestCheckoutPurchase) => boolean
  /** Fires the experiment activation event for an eligible purchase. */
  activate: (purchase: GuestCheckoutPurchase) => void
}

export function shouldEnableGuestCheckout(
  isEligiblePurchase: boolean,
  variation: string | undefined,
): boolean {
  return isEligiblePurchase && variation === 'on'
}

/**
 * Eligible purchases keep Book now for both cohorts. The experiment assigns
 * guest checkout or the login modal after that shared click.
 */
export function shouldShowMemberSavingsCtaInsteadOfBook(
  isLoggedIn: boolean,
  isMetaSearch: boolean,
  isEligiblePurchase: boolean,
): boolean {
  return !isLoggedIn && isMetaSearch && !isEligiblePurchase
}

/**
 * Applies the rollout controls to the shared guest checkout guard. Product
 * flows supply the exact purchase shape because it is not available globally
 * before they initialise a cart.
 */
function useGuestCheckoutEligibility(
  isLoggedIn: boolean | undefined,
  rollout: GuestCheckoutRollout = 'metasearch',
): GuestCheckoutEligibility {
  const utmBp = useAppSelector(state => state.utm.bp)
  const isBotTraffic = useAppSelector(isCurrentRequestBot)
  const isGoogleHotelAdsVerifier = useAppSelector(isGhaVerifierUserAgent)
  const paidSearch = useExperiment(Experiments.growthPaidSearchGuestCheckout, { canActivate: false })
  const metasearch = useExperiment(Experiments.growthMetasearchGuestCheckout, { canActivate: false })
  const { triggerActivation, variation } = rollout === 'paidSearch' ? paidSearch : metasearch
  const isLuxPlusEnabled = paidSearch.variation === 'on'

  const isEligiblePurchase = useCallback((purchase: GuestCheckoutPurchase) => canUseGuestCheckout({
    ...purchase,
    brand: config.BRAND,
    isBotTraffic,
    isGoogleHotelAdsVerifier,
    isLoggedIn: !!isLoggedIn,
    utmBp,
    rollout,
    isLuxPlusEnabled,
  }), [isBotTraffic, isGoogleHotelAdsVerifier, isLoggedIn, utmBp, rollout, isLuxPlusEnabled])

  const canProceedAsGuest = useCallback((purchase: GuestCheckoutPurchase) => {
    const eligiblePurchase = isEligiblePurchase(purchase)
    const canProceed = shouldEnableGuestCheckout(
      eligiblePurchase,
      variation,
    )
    return canProceed
  }, [isEligiblePurchase, variation])

  const activate = useCallback((purchase: GuestCheckoutPurchase) => {
    if (isEligiblePurchase(purchase)) {
      triggerActivation()
    }
  }, [isEligiblePurchase, triggerActivation])

  return { isEligiblePurchase, canProceedAsGuest, activate }
}

export default useGuestCheckoutEligibility
